// SwipeToDeleteRow.tsx — Wisch-Geste auf einer Mail-Zeile (nur Touch).
//
// Wischen nach LINKS legt eine rote Fläche mit Papierkorb-Symbol frei und löscht
// beim Loslassen. Die Richtung entspricht iOS Mail und Outlook Mobile; ein Wisch
// nach rechts tut bewusst nichts.
//
// Warum eine eigene Komponente und kein Hook direkt in der Liste: Die Zeilen
// entstehen in einem `.map()` innerhalb von `renderList` — dort dürfen keine
// Hooks laufen. Jede Zeile braucht aber ihren eigenen Gesten-Zustand.
//
// Die Entscheidungslogik (Achsen-Lock, Auslöseschwelle, Rand-Zonen) liegt in
// swipeGesture.ts und ist dort ohne Gerät testbar.

import React, { useRef, useState } from "react";
import { Delete20Regular } from "@fluentui/react-icons";
import { clampOffset, decideAxis, isEdgeStart, shouldCommit, type Axis } from "./swipeGesture";

interface Props {
    /** Aus ist der Normalfall: Desktop rührt die Geste nicht an. */
    enabled: boolean;
    onDelete: () => void;
    children: React.ReactNode;
}

export const SwipeToDeleteRow: React.FC<Props> = ({ enabled, onDelete, children }) => {
    const wrapRef = useRef<HTMLDivElement>(null);
    const start = useRef<{ x: number; y: number; t: number; axis: Axis } | null>(null);
    const [offset, setOffset] = useState(0);
    const [animating, setAnimating] = useState(false);

    if (!enabled) return <>{children}</>;

    const reset = (withAnimation: boolean) => {
        setAnimating(withAnimation);
        setOffset(0);
        start.current = null;
    };

    const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        // Maus bewusst ausgenommen: auf dem Desktop gibt es das Kontextmenü,
        // und ein Drag mit gedrückter Maustaste würde die Textauswahl stören.
        if (e.pointerType === "mouse") return;
        if (isEdgeStart(e.clientX, window.innerWidth)) return;
        setAnimating(false);
        start.current = { x: e.clientX, y: e.clientY, t: e.timeStamp, axis: "undecided" };
    };

    const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        const s = start.current;
        if (!s) return;

        const dx = e.clientX - s.x;
        const dy = e.clientY - s.y;

        if (s.axis === "undecided") {
            const axis = decideAxis(dx, dy);
            if (axis === "undecided") return;
            s.axis = axis;
            if (axis === "vertical") {
                // Scrollen gewinnt — Geste vollständig aufgeben, damit die Liste
                // normal weiterläuft.
                start.current = null;
                return;
            }
            // Ab hier gehört die Geste uns. Der Pointer wird eingefangen, damit
            // ein Verlassen der Zeile die Geste nicht abreißen lässt.
            e.currentTarget.setPointerCapture?.(e.pointerId);
        }

        const width = wrapRef.current?.offsetWidth ?? 0;
        setOffset(clampOffset(dx, width));
    };

    const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        const s = start.current;
        if (!s || s.axis !== "horizontal") {
            reset(false);
            return;
        }
        const dx = e.clientX - s.x;
        const width = wrapRef.current?.offsetWidth ?? 0;
        e.currentTarget.releasePointerCapture?.(e.pointerId);

        if (shouldCommit(dx, width, e.timeStamp - s.t)) {
            // Zeile nach links aus dem Bild schieben, dann löschen. Die Liste
            // entfernt sie ohnehin; die Bewegung macht nur sichtbar, was passiert.
            setAnimating(true);
            setOffset(-width);
            start.current = null;
            onDelete();
            return;
        }
        reset(true); // zurückfedern
    };

    const revealed = offset < 0;

    return (
        <div
            ref={wrapRef}
            style={{
                position: "relative",
                // Der Browser behält das vertikale Scrollen, gibt aber die
                // horizontale Achse an uns ab.
                touchAction: "pan-y",
            }}
        >
            {/* Aktionsfläche HINTER der Zeile, nicht als Verschiebung des Inhalts:
                so bleibt der Auswahl-Rand am Element und wandert mit. */}
            <div
                aria-hidden
                style={{
                    position: "absolute",
                    inset: 0,
                    display: revealed ? "flex" : "none",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    paddingRight: 22,
                    background: "#c23b3b",
                    color: "#fff",
                }}
            >
                <Delete20Regular />
            </div>
            <div
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={() => reset(true)}
                style={{
                    position: "relative",
                    transform: `translateX(${offset}px)`,
                    transition: animating ? "transform 0.18s ease-out" : "none",
                }}
            >
                {children}
            </div>
        </div>
    );
};
